import {
  Check,
  FolderOpen,
  LoaderCircle,
  RefreshCw,
  Save,
} from "lucide-react";
import { useEffect, useState } from "react";
import type { DeviceSettings, ProviderInventory, ProviderInventoryItem } from "../sdk/types";
import { ProviderIcon } from "./ProviderIcon";
import { useI18n } from "../i18n";

export interface DesktopInitializationPanelProps {
  settings: DeviceSettings | null;
  inventory: ProviderInventory | null;
  inventoryLoading: boolean;
  onRefreshInventory: () => Promise<void>;
  onToggleProvider: (provider: ProviderInventoryItem) => Promise<void>;
  onPickDirectory: () => Promise<string | null>;
  onSaveComputerName: (name: string) => Promise<void>;
  onSaveSessionRoot: (path: string) => Promise<void>;
}

export function DesktopInitializationPanel({
  settings,
  inventory,
  inventoryLoading,
  onRefreshInventory,
  onToggleProvider,
  onPickDirectory,
  onSaveComputerName,
  onSaveSessionRoot,
}: DesktopInitializationPanelProps) {
  const { t } = useI18n();
  const [computerName, setComputerName] = useState(settings?.computerName ?? "");
  const [sessionRoot, setSessionRoot] = useState(settings?.sessionRoot ?? "");
  const [busyProvider, setBusyProvider] = useState<string | null>(null);
  const [saving, setSaving] = useState<"computer" | "root" | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setComputerName(settings?.computerName ?? "");
    setSessionRoot(settings?.sessionRoot ?? "");
  }, [settings]);

  const save = async (kind: "computer" | "root") => {
    const value = (kind === "computer" ? computerName : sessionRoot).trim();
    if (!value || saving) return;
    setSaving(kind);
    setError("");
    try {
      if (kind === "computer") await onSaveComputerName(value);
      else await onSaveSessionRoot(value.replace(/\/$/, ""));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSaving(null);
    }
  };

  const toggleProvider = async (provider: ProviderInventoryItem) => {
    if (!provider.installed || provider.candidate || busyProvider) return;
    setBusyProvider(provider.providerId);
    setError("");
    try {
      await onToggleProvider(provider);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusyProvider(null);
    }
  };

  return (
    <section className="desktop-initialization-panel">
      <header className="desktop-initialization-heading">
        <div>
          <h1>{t("initialization.title")}</h1>
          <p>{t("initialization.detail")}</p>
        </div>
      </header>
      <div className="desktop-initialization-steps">
        <section className="desktop-initialization-step">
          <header>
            <span className="desktop-initialization-step-number">1</span>
            <div>
              <h2>{t("common.agent")}</h2>
              <p>{t("settings.provider.lead")}</p>
            </div>
            <button
              type="button"
              className="desktop-initialization-refresh"
              disabled={inventoryLoading}
              onClick={() => void onRefreshInventory()}
              title={t("settings.provider.refresh")}
              aria-label={t("settings.provider.refresh")}
            >
              <RefreshCw className={inventoryLoading ? "spin" : ""} size={15} />
            </button>
          </header>
          <div className="desktop-initialization-agents">
            {inventoryLoading ? (
              <div className="desktop-initialization-loading"><LoaderCircle className="spin" size={16} />{t("settings.provider.loading")}</div>
            ) : inventory?.providers.length ? inventory.providers.map((provider) => (
              <article className="desktop-initialization-agent" key={provider.providerId}>
                <span className="desktop-initialization-agent-icon"><ProviderIcon provider={provider.providerId} size={18} /></span>
                <span className="desktop-initialization-agent-copy">
                  <strong>{provider.displayName}</strong>
                  <small>{provider.installed ? provider.executablePath ?? t("settings.provider.installed") : provider.candidate ? t("agents.providers.candidate") : t("settings.provider.notInstalled")}</small>
                </span>
                <span className={`desktop-initialization-agent-state ${provider.enabled && provider.installed ? "ready" : ""}`}>
                  {provider.installed && provider.enabled ? <><Check size={13} />{t("settings.provider.enabled")}</> : provider.installed ? t("settings.provider.disabled") : t("settings.provider.notInstalled")}
                </span>
                {provider.installed && !provider.candidate ? <button type="button" disabled={busyProvider !== null} onClick={() => void toggleProvider(provider)}>
                  {busyProvider === provider.providerId ? <LoaderCircle className="spin" size={13} /> : provider.enabled ? t("settings.provider.disable") : t("settings.provider.enable")}
                </button> : null}
              </article>
            )) : (
              <div className="desktop-initialization-loading">{t("settings.provider.emptyInventory")}</div>
            )}
          </div>
        </section>

        <section className="desktop-initialization-step">
          <header>
            <span className="desktop-initialization-step-number">2</span>
            <div>
              <h2>{t("settings.computerName.title")}</h2>
              <p>{t("settings.computerName.detail")}</p>
            </div>
          </header>
          <div className="desktop-initialization-field">
            <input value={computerName} placeholder={t("settings.computerName.placeholder")} onChange={(event) => setComputerName(event.target.value)} />
            <button type="button" disabled={!computerName.trim() || saving !== null} onClick={() => void save("computer")}>
              {saving === "computer" ? <LoaderCircle className="spin" size={13} /> : <Save size={13} />}{t("common.save")}
            </button>
          </div>
        </section>

        <section className="desktop-initialization-step">
          <header>
            <span className="desktop-initialization-step-number">3</span>
            <div>
              <h2>{t("settings.projectRoot.title")}</h2>
              <p>{t("settings.projectRoot.lead")}</p>
            </div>
          </header>
          <div className="desktop-initialization-field">
            <input value={sessionRoot} onChange={(event) => setSessionRoot(event.target.value)} />
            <button type="button" onClick={() => void onPickDirectory().then((path) => { if (path) setSessionRoot(path); })}>
              <FolderOpen size={13} />{t("common.choose")}
            </button>
            <button type="button" disabled={!sessionRoot.trim() || saving !== null} onClick={() => void save("root")}>
              {saving === "root" ? <LoaderCircle className="spin" size={13} /> : <Save size={13} />}{t("common.save")}
            </button>
          </div>
        </section>
      </div>
      {error ? <p className="desktop-initialization-error" role="alert">{error}</p> : null}
    </section>
  );
}
